import React from "react";
import { DetailItem } from "../../../components/ModalDetailsGeneric/ModalDetailsGeneric";
import {
  EMPTY_VALUE,
  TitleValue,
  dash,
  getStateKeys,
  buildRequestNumberItem,
  buildStateLabelItem,
} from "../../../components/ModalDetailsGeneric/detailItemsShared";
import { formatCurrency } from "../../../utils/currencyFormatter";

interface IcmsHeader {
  distributor?: TitleValue;
  requestNumber?: TitleValue;
  requestDate?: TitleValue;
  requester?: TitleValue;
}

interface IcmsContentLabels {
  petition?: TitleValue;
  xmlNotes?: TitleValue;
  refundValues?: TitleValue;
  requestNote?: TitleValue;
  analistNote?: TitleValue;
}

interface IcmsStateValues {
  petition?: string;
  xmlNotes?: string;
  refundValues?: string;
}

export interface IcmsModalData extends IcmsStateValues {
  header: IcmsHeader;
  content: IcmsContentLabels;
  requestNote?: string;
  analistNote?: string;
  status?: string;
  // Per-state values (e.g. approved / orded) present when withTab is true.
  [key: string]: unknown;
}

/**
 * Parses a refund value into a number. The value always arrives as a canonical
 * numeric string produced by `number.toString()` (dot decimal, no thousands
 * separator, e.g. "1234.56"), since it originates from a numeric API field.
 * Returns undefined when it cannot produce a finite number.
 */
function parseRefundValue(value?: string): number | undefined {
  if (value == null || value.trim() === "") return undefined;
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : undefined;
}

function buildStateFields(
  content: IcmsContentLabels,
  state: IcmsStateValues
): DetailItem[] {
  const refund = parseRefundValue(state.refundValues);
  return [
    {
      label: content.petition?.title ?? "Petição",
      value: dash(state.petition),
    },
    {
      label: content.xmlNotes?.title ?? "Notas",
      value: dash(state.xmlNotes),
    },
    {
      label: content.refundValues?.title ?? "Valor do ressarcimento",
      value: refund != null ? formatCurrency(refund) : EMPTY_VALUE,
    },
  ];
}

/**
 * Builds the flat DetailItem[] for the tax reimbursement detail modal so it can
 * be rendered by the shared DetailsModal (ModalDetailsGeneric), matching the
 * Notas Fiscais modal design.
 *
 * Field mapping:
 * - header.requestNumber / requestDate / requester -> DetailItems
 * - petition / xmlNotes / refundValues -> DetailItems (per approved/orded state
 *   when withTab is true, otherwise the top-level values)
 * - requestNote / analistNote -> full-width DetailItems, only when present
 */
export function buildTaxReimbursementDetailItems(
  data: IcmsModalData,
  withTab: boolean,
  statusNode?: React.ReactNode
): DetailItem[] {
  const { header, content } = data;
  const items: DetailItem[] = [];

  items.push(
    buildRequestNumberItem(
      header?.requestNumber?.title ?? "ID do Pedido",
      header?.requestNumber?.value,
      statusNode
    )
  );
  items.push({
    label: header?.requestDate?.title ?? "Data da Solicitação",
    value: dash(header?.requestDate?.value),
  });
  items.push({
    label: header?.requester?.title ?? "Solicitado por",
    value: dash(header?.requester?.value),
  });

  const stateKeys = getStateKeys(data);

  if (withTab && stateKeys.length > 0) {
    stateKeys.forEach((key) => {
      items.push(buildStateLabelItem(key));
      items.push(...buildStateFields(content, data[key] as IcmsStateValues));
    });
  } else {
    items.push(...buildStateFields(content, data));
  }

  if (data.requestNote) {
    items.push({
      label: content.requestNote?.title ?? "Observação",
      value: data.requestNote,
      fullWidth: true,
    });
  }
  if (data.analistNote) {
    items.push({
      label: content.analistNote?.title ?? "Observação (analista)",
      value: data.analistNote,
      fullWidth: true,
    });
  }

  return items;
}
